import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart, ArrowLeft, Users, Calendar, DollarSign, CheckCircle,
  Mail, Phone, MapPin, Clock, Send, Edit2, Trash2, 
  MoreVertical, ChevronDown, ChevronUp, FileText, Sparkles,
  User, Crown, Activity, MessageCircle, Save, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AdminClientDetail = ({ user }) => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({ tasks: true, guests: false, budget: false });
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    try {
      const res = await axios.get(`${API}/admin/client/${clientId}`, {
        headers: { 'admin-id': user.id }
      });
      setClient(res.data);
      
      // Initialize edit data
      const clientUser = res.data.user;
      const weddingPlan = res.data.wedding_plan || {};
      setEditData({
        partner1_name: clientUser?.partner1_name || '',
        partner2_name: clientUser?.partner2_name || '',
        email: clientUser?.email || '',
        wedding_date: weddingPlan?.wedding_date?.split('T')[0] || '',
        venue: weddingPlan?.venue || '',
        venue_location: weddingPlan?.venue_location || '',
        total_budget: weddingPlan?.total_budget || ''
      });
    } catch (error) {
      console.error('Error loading client:', error);
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (emailType) => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-milestone-email`, null, {
        headers: { 'admin-id': user.id },
        params: {
          user_id: clientId,
          email_type: emailType,
          custom_message: emailMessage || undefined
        }
      });
      toast.success('Email sent successfully!');
      setEmailMessage('');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Update user info
      await axios.put(`${API}/users/${clientId}`, {
        partner1_name: editData.partner1_name,
        partner2_name: editData.partner2_name,
        email: editData.email
      }, {
        headers: { 'admin-id': user.id }
      });
      
      // Update wedding plan
      await axios.put(`${API}/wedding-plans/${clientId}`, {
        wedding_date: editData.wedding_date || null,
        venue: editData.venue || null,
        venue_location: editData.venue_location || null,
        total_budget: editData.total_budget ? parseFloat(editData.total_budget) : null
      });
      
      toast.success('Client information saved!');
      setEditMode(false);
      loadClientData(); // Refresh data
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Heart className="w-12 h-12 text-[#881337] animate-pulse" />
          <p className="text-white/60">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <p className="text-white/60">Client not found</p>
      </div>
    );
  }

  const { user: clientUser, wedding_plan, wedding_details, stats, tasks, guests, budget_items, timeline, vendors } = client;

  const daysUntilWedding = wedding_plan?.wedding_date 
    ? (() => {
        const [year, month, day] = wedding_plan.wedding_date.split('-').map(Number);
        const wedding = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return Math.ceil((wedding - today) / (1000 * 60 * 60 * 24));
      })()
    : null;

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      {/* Top Navigation */}
      <nav className="bg-[#1C1917] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="flex items-center gap-3">
                <Heart className="text-[#881337]" size={24} fill="#881337" />
                <span className="text-lg font-serif text-white">VowFlow</span>
                <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full">ADMIN</span>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {editMode ? (
                <>
                  <Button
                    onClick={() => setEditMode(false)}
                    variant="outline"
                    className="border-white/20 text-white"
                  >
                    <X size={16} className="mr-2" /> Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setEditMode(true)}
                    variant="outline"
                    className="border-white/20 text-white"
                  >
                    <Edit2 size={16} className="mr-2" /> Edit Client
                  </Button>
                  <Button
                    onClick={() => handleSendEmail('checkin')}
                    disabled={sendingEmail}
                    className="bg-[#881337] hover:bg-[#9F1239]"
                  >
                    <Mail size={16} className="mr-2" />
                    Send Check-in Email
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Client Header */}
        <div className="bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-2xl p-8 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-serif text-white">
                  {clientUser.partner1_name} & {clientUser.partner2_name}
                </h1>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  clientUser.subscription_tier === 'premium' 
                    ? 'bg-amber-400 text-amber-900' 
                    : 'bg-white/20 text-white'
                }`}>
                  {clientUser.subscription_tier === 'premium' ? '★ Premium' : 'Essentials'}
                </span>
              </div>
              <p className="text-rose-200">{clientUser.email}</p>
              <p className="text-rose-200/60 text-sm mt-1">
                Member since {new Date(clientUser.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
            
            {daysUntilWedding !== null && daysUntilWedding >= 0 && (
              <div className="text-right">
                <p className="text-6xl font-serif text-white font-light">{daysUntilWedding}</p>
                <p className="text-rose-200">days until wedding</p>
              </div>
            )}
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-rose-200 text-sm">Progress</p>
              <p className="text-2xl font-serif text-white">{stats.progress}%</p>
              <div className="w-full bg-white/20 rounded-full h-1.5 mt-2">
                <div className="bg-white h-1.5 rounded-full" style={{ width: `${stats.progress}%` }} />
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-rose-200 text-sm">Tasks</p>
              <p className="text-2xl font-serif text-white">{stats.completed_tasks}/{stats.total_tasks}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-rose-200 text-sm">Guests</p>
              <p className="text-2xl font-serif text-white">{stats.total_guests}</p>
              <p className="text-rose-200/60 text-xs">{stats.confirmed_guests} confirmed</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-rose-200 text-sm">Budget</p>
              <p className="text-2xl font-serif text-white">${stats.spent_budget?.toLocaleString() || 0}</p>
              <p className="text-rose-200/60 text-xs">of ${stats.total_budget?.toLocaleString() || 0}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'timeline', 'tasks', 'guests', 'budget', 'communication'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-[#881337] text-white'
                  : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'overview' && (
              <>
                {/* Wedding Details Card */}
                <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                  <h2 className="text-lg font-serif text-white mb-4">Wedding Details</h2>
                  {editMode ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/40 text-sm block mb-1">Partner 1 Name</label>
                        <Input
                          value={editData.partner1_name}
                          onChange={(e) => setEditData({...editData, partner1_name: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-sm block mb-1">Partner 2 Name</label>
                        <Input
                          value={editData.partner2_name}
                          onChange={(e) => setEditData({...editData, partner2_name: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-white/40 text-sm block mb-1">Email</label>
                        <Input
                          type="email"
                          value={editData.email}
                          onChange={(e) => setEditData({...editData, email: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-sm block mb-1">Wedding Date</label>
                        <Input
                          type="date"
                          value={editData.wedding_date}
                          onChange={(e) => setEditData({...editData, wedding_date: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-sm block mb-1">Total Budget</label>
                        <Input
                          type="number"
                          value={editData.total_budget}
                          onChange={(e) => setEditData({...editData, total_budget: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-sm block mb-1">Venue</label>
                        <Input
                          value={editData.venue}
                          onChange={(e) => setEditData({...editData, venue: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="Venue name"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-sm block mb-1">Location (City, State)</label>
                        <Input
                          value={editData.venue_location}
                          onChange={(e) => setEditData({...editData, venue_location: e.target.value})}
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="City, State"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-white/40 text-sm">Wedding Date</p>
                        <p className="text-white">
                          {wedding_plan?.wedding_date 
                            ? (() => {
                                const [year, month, day] = wedding_plan.wedding_date.split('-').map(Number);
                                return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                              })()
                            : 'Not set'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-white/40 text-sm">Venue</p>
                        <p className="text-white">{wedding_plan?.venue || wedding_details?.ceremony_location || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-sm">Location</p>
                        <p className="text-white">{wedding_plan?.venue_location || wedding_details?.ceremony_address || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-sm">Expected Guests</p>
                        <p className="text-white">{wedding_plan?.guest_count || wedding_details?.estimated_guest_count || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-sm">Ceremony Time</p>
                        <p className="text-white">{wedding_details?.ceremony_start_time || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-sm">Reception Time</p>
                        <p className="text-white">{wedding_details?.reception_start_time || 'Not set'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Recent Tasks */}
                <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-serif text-white">Recent Tasks</h2>
                    <button onClick={() => setActiveTab('tasks')} className="text-sm text-[#881337]">View All</button>
                  </div>
                  <div className="space-y-3">
                    {tasks.slice(0, 5).map((task) => (
                      <div key={task.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          task.completed ? 'bg-green-500 border-green-500' : 'border-white/30'
                        }`}>
                          {task.completed && <CheckCircle size={12} className="text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm ${task.completed ? 'text-white/50 line-through' : 'text-white'}`}>
                            {task.title}
                          </p>
                          {task.phase && <p className="text-white/30 text-xs">{task.phase}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Vendors */}
                {vendors && vendors.length > 0 && (
                  <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                    <h2 className="text-lg font-serif text-white mb-4">Vendors ({vendors.length})</h2>
                    <div className="grid grid-cols-2 gap-3">
                      {vendors.map((vendor) => (
                        <div key={vendor.id} className="p-3 bg-white/5 rounded-xl">
                          <p className="text-white text-sm font-medium">{vendor.name}</p>
                          <p className="text-white/40 text-xs">{vendor.category}</p>
                          {vendor.contact_email && (
                            <p className="text-white/60 text-xs mt-1">{vendor.contact_email}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'tasks' && (
              <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                <h2 className="text-lg font-serif text-white mb-4">All Tasks ({stats.total_tasks})</h2>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        task.completed ? 'bg-green-500 border-green-500' : 'border-white/30'
                      }`}>
                        {task.completed && <CheckCircle size={12} className="text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm ${task.completed ? 'text-white/50 line-through' : 'text-white'}`}>
                          {task.title}
                        </p>
                        {task.description && <p className="text-white/30 text-xs mt-0.5">{task.description}</p>}
                      </div>
                      {task.phase && (
                        <span className="text-xs bg-white/10 text-white/50 px-2 py-1 rounded-full">{task.phase}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'guests' && (
              <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                <h2 className="text-lg font-serif text-white mb-4">Guest List ({stats.total_guests})</h2>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {guests.map((guest) => (
                    <div key={guest.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white">
                        {guest.name?.[0] || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{guest.name}</p>
                        <p className="text-white/40 text-xs">{guest.email}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        guest.rsvp_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                        guest.rsvp_status === 'declined' ? 'bg-red-500/20 text-red-400' :
                        'bg-white/10 text-white/50'
                      }`}>
                        {guest.rsvp_status || 'Pending'}
                      </span>
                      {guest.side && (
                        <span className="text-xs bg-white/10 text-white/50 px-2 py-1 rounded-full">{guest.side}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'budget' && (
              <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-serif text-white">Budget Items</h2>
                  <div className="text-right">
                    <p className="text-white text-lg font-serif">${stats.spent_budget?.toLocaleString() || 0} / ${stats.total_budget?.toLocaleString() || 0}</p>
                    <p className="text-white/40 text-xs">spent / total</p>
                  </div>
                </div>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {budget_items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <DollarSign size={16} className="text-white/40" />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{item.item_name}</p>
                        <p className="text-white/40 text-xs">{item.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm">${item.actual_cost?.toLocaleString() || item.estimated_cost?.toLocaleString()}</p>
                        {item.actual_cost && item.actual_cost !== item.estimated_cost && (
                          <p className="text-white/40 text-xs">Est: ${item.estimated_cost?.toLocaleString()}</p>
                        )}
                      </div>
                      {item.paid && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Paid</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'communication' && (
              <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                <h2 className="text-lg font-serif text-white mb-4">Send Message</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleSendEmail('reminder')}
                      disabled={sendingEmail}
                      className="p-4 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors"
                    >
                      <Clock size={20} className="text-amber-400 mb-2" />
                      <p className="text-white text-sm font-medium">Task Reminder</p>
                      <p className="text-white/40 text-xs">Remind them about incomplete tasks</p>
                    </button>
                    <button
                      onClick={() => handleSendEmail('checkin')}
                      disabled={sendingEmail}
                      className="p-4 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors"
                    >
                      <MessageCircle size={20} className="text-blue-400 mb-2" />
                      <p className="text-white text-sm font-medium">Check-in</p>
                      <p className="text-white/40 text-xs">Friendly progress check-in</p>
                    </button>
                    <button
                      onClick={() => handleSendEmail('congratulations')}
                      disabled={sendingEmail}
                      className="p-4 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors"
                    >
                      <Sparkles size={20} className="text-pink-400 mb-2" />
                      <p className="text-white text-sm font-medium">Congratulations</p>
                      <p className="text-white/40 text-xs">Celebrate a milestone</p>
                    </button>
                    <button
                      onClick={() => {}}
                      className="p-4 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors"
                    >
                      <Edit2 size={20} className="text-green-400 mb-2" />
                      <p className="text-white text-sm font-medium">Custom Message</p>
                      <p className="text-white/40 text-xs">Write a personalized email</p>
                    </button>
                  </div>
                  
                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Custom Message (optional)</label>
                    <Textarea
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      placeholder="Add a personal note to your message..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
                    />
                  </div>
                  
                  <Button
                    onClick={() => handleSendEmail('custom')}
                    disabled={sendingEmail || !emailMessage}
                    className="w-full bg-[#881337] hover:bg-[#9F1239]"
                  >
                    <Send size={16} className="mr-2" />
                    Send Custom Email
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'timeline' && timeline && (
              <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
                <h2 className="text-lg font-serif text-white mb-4">Day-of Timeline</h2>
                <div className="space-y-3">
                  {(timeline.events || []).map((event, idx) => (
                    <div key={idx} className="flex gap-4 p-3 bg-white/5 rounded-xl">
                      <div className="text-[#881337] font-medium text-sm w-20">{event.time}</div>
                      <div>
                        <p className="text-white text-sm font-medium">{event.title}</p>
                        {event.description && <p className="text-white/40 text-xs mt-0.5">{event.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
              <h3 className="text-white font-medium mb-4">Contact Info</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-white/70">
                  <Mail size={16} className="text-white/40" />
                  <span className="text-sm">{clientUser.email}</span>
                </div>
                {wedding_details?.venue_coordinator_contact && (
                  <div className="flex items-center gap-3 text-white/70">
                    <Phone size={16} className="text-white/40" />
                    <span className="text-sm">{wedding_details.venue_coordinator_contact}</span>
                  </div>
                )}
                {wedding_plan?.venue_location && (
                  <div className="flex items-center gap-3 text-white/70">
                    <MapPin size={16} className="text-white/40" />
                    <span className="text-sm">{wedding_plan.venue_location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
              <h3 className="text-white font-medium mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full p-3 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors flex items-center gap-3">
                  <Crown size={16} className="text-amber-400" />
                  <span className="text-white text-sm">Upgrade to Premium</span>
                </button>
                <button className="w-full p-3 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors flex items-center gap-3">
                  <FileText size={16} className="text-blue-400" />
                  <span className="text-white text-sm">Export Data</span>
                </button>
                <button className="w-full p-3 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors flex items-center gap-3">
                  <Activity size={16} className="text-green-400" />
                  <span className="text-white text-sm">View Activity Log</span>
                </button>
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-6">
              <h3 className="text-white font-medium mb-4">Account</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/40">Plan</span>
                  <span className={`${clientUser.subscription_tier === 'premium' ? 'text-amber-400' : 'text-white'}`}>
                    {clientUser.subscription_tier === 'premium' ? '★ Premium' : 'Essentials'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">Member Since</span>
                  <span className="text-white">{new Date(clientUser.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">User ID</span>
                  <span className="text-white/60 text-xs font-mono">{clientUser.id.slice(0, 8)}...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClientDetail;
